import React, { useRef, useEffect, useState } from 'react'
import { Bold, Italic, List, ListOrdered, Quote, Link, Undo, Redo } from 'lucide-react'
import { cn } from 'lib/utils'

interface SimpleRichTextEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  error?: string
  label?: string
  required?: boolean
  disabled?: boolean
  height?: number
}

export function SimpleRichTextEditor({
  value,
  onChange,
  placeholder = "Entrez votre texte...",
  className,
  error,
  label,
  required = false,
  disabled = false,
  height = 300
}: SimpleRichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null)
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value
    }
  }, [value])

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML)
    }
  }

  const execCommand = (command: string, value?: string) => {
    if (editorRef.current) {
      editorRef.current.focus()
      document.execCommand(command, false, value)
      handleInput()
    }
  }

  const insertLink = () => {
    const url = prompt('Entrez l\'URL du lien:')
    if (url) {
      execCommand('createLink', url)
    }
  }

  const toolbarButtons = [
    { icon: Bold, command: 'bold', title: 'Gras' },
    { icon: Italic, command: 'italic', title: 'Italique' },
    { icon: List, command: 'insertUnorderedList', title: 'Liste à puces' },
    { icon: ListOrdered, command: 'insertOrderedList', title: 'Liste numérotée' },
    { icon: Quote, command: 'formatBlock', value: 'blockquote', title: 'Citation' },
    { icon: Link, action: insertLink, title: 'Lien' },
    { icon: Undo, command: 'undo', title: 'Annuler' },
    { icon: Redo, command: 'redo', title: 'Refaire' }
  ]

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      <div className={cn(
        "border border-input rounded-md bg-background overflow-hidden",
        error && "border-destructive",
        disabled && "opacity-50 cursor-not-allowed",
        isFocused && "ring-2 ring-ring ring-offset-2"
      )}>
        {/* Toolbar */}
        <div className="flex items-center gap-1 p-2 border-b bg-muted/50">
          {toolbarButtons.map((button, index) => (
            <button
              key={index}
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (button.action) {
                  button.action()
                } else {
                  execCommand(button.command, button.value)
                }
              }}
              disabled={disabled}
              className="p-2 hover:bg-accent hover:text-accent-foreground rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={button.title}
            >
              <button.icon className="h-4 w-4" />
            </button>
          ))}
        </div>
        
        {/* Editor */}
        <div
          ref={editorRef}
          contentEditable={!disabled}
          onInput={handleInput}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className={cn(
            "p-3 min-h-[200px] focus:outline-none",
            disabled && "cursor-not-allowed"
          )}
          style={{ minHeight: `${height}px` }}
          data-placeholder={placeholder}
          suppressContentEditableWarning={true}
        />
      </div>
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
      
      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        [contenteditable] ul {
          list-style-type: disc;
          margin-left: 20px;
          margin-top: 8px;
          margin-bottom: 8px;
        }
        [contenteditable] ol {
          list-style-type: decimal;
          margin-left: 20px;
          margin-top: 8px;
          margin-bottom: 8px;
        }
        [contenteditable] li {
          margin: 4px 0;
        }
        [contenteditable] blockquote {
          border-left: 4px solid #e5e7eb;
          padding-left: 16px;
          margin: 8px 0;
          font-style: italic;
          color: #6b7280;
        }
        [contenteditable] strong {
          font-weight: bold;
        }
        [contenteditable] em {
          font-style: italic;
        }
        [contenteditable] a {
          color: #3b82f6;
          text-decoration: underline;
        }
      `}</style>
    </div>
  )
}
